import React, { useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { AuthContext } from "../../contexts";
import { toastOptions } from "../../assets/constants";
import { TimeTableHeader, TimeTableEditRow, TimeTableViewRow, TimeTableFooter, TimeTableAutomationRow, SortableTableRow } from "./components";
import { useEntryOperations, useSortedEntries, useRecentItems } from "./hooks";
import { getEntryId, normalizeDuration } from "./utils/trackingUtils";

dayjs.extend(isoWeek);
dayjs.extend(utc);
dayjs.extend(timezone);

const DEBOUNCE_DELAY = 300;

const ROWS_PER_PAGE_OPTIONS = [20, 50, 100, 200];

const TimeTable = ({
    entries,
    loading,
    setEntries,
    projects,
    tasks,
    sections,
    handleAddEntry,
    tab,
    automations,
    setAutomations,
    models,
    setModels,
    filterProject,
    searchQuery,
    activeDay,
    setActiveDay,
    setTab,
    onReorderEntries,
}) => {
    const { token, refreshDashboard } = useContext(AuthContext);
    const [localComments, setLocalComments] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const debounceTimerRef = useRef(null);

    const { filteredEntries, sortConfig, requestSort, isDragDisabled } = useSortedEntries(entries, {
        tab,
        activeDay,
        filterProject,
        searchQuery,
        projects,
    });

    const totalPages = tab === 1 ? Math.ceil(filteredEntries.length / rowsPerPage) : 1;
    const paginatedEntries = useMemo(() => {
        if (tab === 0) return filteredEntries;
        const startIndex = (currentPage - 1) * rowsPerPage;
        return filteredEntries.slice(startIndex, startIndex + rowsPerPage);
    }, [tab, filteredEntries, currentPage, rowsPerPage]);

    const hasTempEntries = entries.some((e) => e.tempId);

    useEffect(() => {
        setCurrentPage(1);
    }, [filterProject, searchQuery, tab]);

    useEffect(() => {
        if (hasTempEntries && tab === 1) {
            setCurrentPage(1);
        }
    }, [hasTempEntries, tab]);

    const { handleEditEntry, handleActivateEditing, handleCancelEdit, handleDeleteEntry, handleDuplicateEntry, handleSubmitEntry } = useEntryOperations({
        entries,
        setEntries,
        token,
        refreshDashboard,
        activeDay,
    });

    const { addRecentProject, addRecentTask, sortedProjects, sortedTasks } = useRecentItems(projects, tasks);

    const handleSubmitWithRecents = useCallback(
        (entry) => {
            addRecentProject(entry.projectId);
            addRecentTask(entry.taskId);
            handleSubmitEntry(entry);
        },
        [addRecentProject, addRecentTask, handleSubmitEntry]
    );

    const updateCommentsInEntries = useCallback(
        (id, comments) => {
            setEntries((prev) => prev.map((e) => (getEntryId(e) === id ? { ...e, comments } : e)));
        },
        [setEntries]
    );

    const handleCommentsChange = useCallback(
        (id, comments) => {
            setLocalComments((prev) => ({ ...prev, [id]: comments }));

            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            debounceTimerRef.current = setTimeout(() => {
                updateCommentsInEntries(id, comments);
            }, DEBOUNCE_DELAY);
        },
        [updateCommentsInEntries]
    );

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const newLocalComments = {};
        entries.forEach((entry) => {
            const entryId = getEntryId(entry);
            if (localComments[entryId] === undefined) {
                newLocalComments[entryId] = entry.comments || "";
            }
        });
        if (Object.keys(newLocalComments).length > 0) {
            setLocalComments((prev) => ({ ...prev, ...newLocalComments }));
        }
    }, [entries]);

    const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

    const handleDragEnd = useCallback(
        async (event) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;

            const activeIndex = filteredEntries.findIndex((entry) => getEntryId(entry) === active.id);
            const overIndex = filteredEntries.findIndex((entry) => getEntryId(entry) === over.id);

            if (activeIndex === -1 || overIndex === -1) return;

            const reorderedFiltered = [...filteredEntries];
            const [movedEntry] = reorderedFiltered.splice(activeIndex, 1);
            reorderedFiltered.splice(overIndex, 0, movedEntry);

            const baseOrder = Math.min(...reorderedFiltered.map((e) => (typeof e.customOrder === "number" ? e.customOrder : 0)));
            const updatedFilteredEntries = reorderedFiltered.map((entry, index) => ({
                ...entry,
                customOrder: baseOrder + index,
            }));

            setEntries((prev) => {
                const newEntries = prev.map((entry) => {
                    const updatedEntry = updatedFilteredEntries.find((filtered) => getEntryId(filtered) === getEntryId(entry));
                    return updatedEntry || entry;
                });
                onReorderEntries?.(newEntries);
                return newEntries;
            });

            const orderUpdates = updatedFilteredEntries.filter((entry) => entry.id && !entry.tempId).map((entry) => ({ id: entry.id, customOrder: entry.customOrder }));

            if (orderUpdates.length > 0) {
                try {
                    await axios.put(`${process.env.REACT_APP_UPA_API_HOST}tracking/entries/order`, { orderUpdates }, { headers: { Authorization: `Bearer ${token}` } });
                    refreshDashboard();
                    toast.success("Ordre des tâches sauvegardé", toastOptions);
                } catch {
                    toast.error("Erreur lors de la sauvegarde de l'ordre des tâches", toastOptions);
                }
            }
        },
        [filteredEntries, setEntries, onReorderEntries, token, refreshDashboard]
    );

    const handleKeyDown = useCallback(
        (e, entry) => {
            if (e.target.classList.contains("number-input") && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
                e.preventDefault();
                let parsed = normalizeDuration(entry.duration);
                parsed = e.key === "ArrowUp" ? parsed + 0.25 : Math.max(0, parsed - 0.25);
                handleEditEntry(getEntryId(entry), { ...entry, duration: parsed });
                return;
            }

            if ((e.ctrlKey || e.metaKey) && e.key === "s") {
                e.preventDefault();
                if (entry.tempId || entry.isEditing) {
                    handleSubmitWithRecents(entry);
                }
                return;
            }

            if ((e.ctrlKey || e.metaKey) && e.key === "d") {
                e.preventDefault();
                if (!entry.tempId && entry.id) {
                    handleDuplicateEntry(entry);
                }
                return;
            }

            if (e.key === "Enter" && (e.target.classList.contains("comment-input") || e.target.classList.contains("number-input"))) {
                e.preventDefault();
                e.stopPropagation();
                handleSubmitWithRecents(entry);
            } else if (e.key === "Escape") {
                e.preventDefault();
                handleCancelEdit(getEntryId(entry));
            }
        },
        [handleEditEntry, handleSubmitWithRecents, handleCancelEdit, handleDuplicateEntry]
    );

    const createKeyDownHandler = useCallback((entry) => (e) => handleKeyDown(e, entry), [handleKeyDown]);

    const activeAutomations = useMemo(() => automations.filter((a) => a.isActive && activeDay.isAfter(dayjs()) && a.repeatEvery[activeDay.isoWeekday() - 1] === 1), [automations, activeDay]);

    // if (loading) {
    //     return (
    //         <div className="loader-container absolute">
    //             <CircularProgress size={30} />
    //         </div>
    //     );
    // }

    return (
        <>
            <div className="scrollable-table-container list-container">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={paginatedEntries.map((entry) => getEntryId(entry))} strategy={verticalListSortingStrategy}>
                        <table className="custom-tracking-table">
                            <TimeTableHeader sortConfig={sortConfig} requestSort={requestSort} hasEntries={paginatedEntries.length > 0} isDragDisabled={isDragDisabled} />
                            <tbody>
                                {paginatedEntries.map((entry) => (
                                    <SortableTableRow
                                        key={getEntryId(entry)}
                                        entry={entry}
                                        isDragDisabled={isDragDisabled}
                                        onKeyDown={entry.isEditing || entry.tempId ? createKeyDownHandler(entry) : undefined}
                                    >
                                        {entry.tempId || entry.isEditing ? (
                                            <TimeTableEditRow
                                                entry={entry}
                                                projects={sortedProjects}
                                                tasks={sortedTasks}
                                                sections={sections}
                                                localComments={localComments}
                                                onEdit={handleEditEntry}
                                                onCommentsChange={handleCommentsChange}
                                                onSubmit={handleSubmitWithRecents}
                                                onCancel={handleCancelEdit}
                                                onDelete={handleDeleteEntry}
                                                onKeyDown={handleKeyDown}
                                                tab={tab}
                                                activeDay={activeDay}
                                                setActiveDay={setActiveDay}
                                            />
                                        ) : (
                                            <TimeTableViewRow entry={entry} projects={projects} onEdit={handleActivateEditing} onDuplicate={handleDuplicateEntry} onDelete={handleDeleteEntry} />
                                        )}
                                    </SortableTableRow>
                                ))}
                                {tab === 0 &&
                                    activeAutomations.map((automation, index) => (
                                        <TimeTableAutomationRow
                                            key={`automation-${index}`}
                                            automation={automation}
                                            projects={projects}
                                            tasks={tasks}
                                            activeDay={activeDay}
                                            hasEntries={paginatedEntries.length > 0}
                                        />
                                    ))}
                            </tbody>
                        </table>
                    </SortableContext>
                </DndContext>
                <TimeTableFooter
                    entries={entries}
                    filteredEntries={filteredEntries}
                    tab={tab}
                    models={models}
                    setModels={setModels}
                    projects={projects}
                    sections={sections}
                    tasks={tasks}
                    setEntries={setEntries}
                    automations={automations}
                    setAutomations={setAutomations}
                    activeDay={activeDay}
                    onAddEntry={handleAddEntry}
                />
                {tab === 1 && filteredEntries.length > 0 && (
                    <div className="pagination-container">
                        <div className="current-page-info">
                            <span>
                                Page{" "}
                                <strong>
                                    {currentPage} sur {totalPages}
                                </strong>
                            </span>
                        </div>
                        <div className="page-switcher">
                            <button className="pagination-button" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                                {"<<"}
                            </button>
                            <button className="pagination-button" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
                                {"<"}
                            </button>
                            <button className="pagination-button" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                                {">"}
                            </button>
                            <button className="pagination-button" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                                {">>"}
                            </button>
                        </div>
                        <div className="rows-by-page">
                            <span>Lignes par page</span>
                            <select
                                value={rowsPerPage}
                                className="pagination-button select"
                                onChange={(e) => {
                                    setRowsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                            >
                                {ROWS_PER_PAGE_OPTIONS.map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        {pageSize}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default TimeTable;
